import {property, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {SessionStorage} from "../../../common/clientStorage";
import {resolve} from "../../../container";
import {formElementEvent, formSubscriptionEvent, initFormElementEvent} from "../formEvents";
import type {InputType} from "../commons";
import {FormPathAggregator} from "../commons";
import {type FormValidationConfig, InputElementType, ValidationError, WarningLevel} from "../validations/validations";
import type {DirectiveResult} from "lit/directive.js";
import {classMap} from "lit/directives/class-map.js";
import {ValidationFactory} from "../validations/validationFactory";
import type {PropertyMap} from "../../../common/utils/objects";
import {EqualHeightManager} from "../../../page/elements/equalHeight";
import {ManagingResources} from "../../../common/lifetime";
import {generateUuid} from "../../../common/crypto";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {isArray} from "../../../bootstrap/common/arrays";
import {UnLitElement} from "../../../common/elements";

export abstract class FormInputElement<T extends InputType> extends ManagingResources(UnLitElement) {

    @property({attribute: "input-id"})
    protected inputId: string;
    @property({attribute: "label-suffix-popover-id"})
    protected labelSuffixPopoverId: string;
    @property({attribute: "condition"})
    private condition: string;
    @property({attribute: false})
    protected validations: FormValidationConfig[];
    @property({attribute: "enhance-values", type: String})
    protected enhanceValues: string = "true";
    @property({reflect: true, type: Boolean})
    protected required: boolean;

    @state()
    protected value: T;
    @state()
    protected isValidatable: boolean;
    @state()
    protected validationFailures: ValidationFailure[];
    @state()
    protected type: Nullable<InputElementType>;
    @state()
    protected customAttributes: PropertyMap<string>;

    protected path: string[];
    private offset: number;
    protected formUUId: string;

    protected constructor(
        private emptyValue: T,
        private equalHeightManager: EqualHeightManager = resolve(EqualHeightManager),
        private sessionStorage: SessionStorage = resolve(SessionStorage)
    ) {
        super();
        this.validations = [];
        this.isValidatable = false;
        this.validationFailures = [];
        this.required = false;
        this.type = null;
        this.customAttributes = {};
        this.formUUId = generateUuid();
    }

    protected abstract preset(): Maybe<T>;

    public abstract focusInput(): void;

    public connectedCallback(): void {
        super.connectedCallback();
        if (this.condition) {
            this.dispatchEvent(formSubscriptionEvent(this.condition, result => this.changeCondition(result)));
        }

        this.path = this.computePath();
        this.initValue();
    }

    private computePath(): string[] {
        const formPathAggregator = new FormPathAggregator(this.inputId);
        this.dispatchEvent(initFormElementEvent(formPathAggregator, this.validate));
        return formPathAggregator.path;
    }

    private initValue(): void {
        this.value = this.sessionStorage.fetchFromPath<T>(this.path) ?? this.preset() ?? this.emptyValue;
        this.isValidatable = this.hasPresentValue(this.value);
        this.updateValidState(this.value);
        this.sendInputEvent(this.value);
    }

    private hasPresentValue(value: any): boolean {
        if (isArray(value)) {
            return value.isEmpty() || value.every(v => !!v);
        }
        return !!this.value;
    }

    protected updated(changedProperties: PropertyValues): void {
        const element = this.querySelector<HTMLElement>(".validation-messages")!;
        if (element) {
            this.equalHeightManager.registerResponsive(element, breakpoint => {
                if (!this.offset || breakpoint) {
                    this.offset = this.getBoundingClientRect().top;
                }
                return "validation-messages-" + this.offset;
            }, false);
        }
    }

    protected getFullId(subId?: string): string {
        let id = this.path.join("-");
        if (subId) {
            id = id + "-" + subId;
        }
        return id;
    }

    protected updateValue(inputValue: T): void {
        if (this.value === inputValue) {
            return;
        }
        this.value = inputValue;
        this.updateValidState(inputValue);
        this.isValidatable = true;
        this.publishValue(inputValue);
    }

    protected formId(): string {
        return this.path[0];
    }

    protected publishValue(inputValue: T): void {
        this.sessionStorage.saveInPath(this.formId(), this.path.slice(1), inputValue);
        this.sendInputEvent(inputValue);
    }

    protected renderValidationMessages(): TemplateResult {
        const texts = this.isInErrorState() || this.isInWarningState()
            ? this.renderValidationTexts()
            : null;

        return html`
            <div class="validation-messages">
                ${this.renderValidationIcon()}
                <div class="error-messages">${texts}</div>
            </div>
        `;
    }

    private renderValidationTexts(): TemplateResult {
        return html`
            ${(this.renderErrors())}
            ${(this.renderWarnings())}
        `;
    }

    private renderErrors(): TemplateResult[] {
        return this.validationFailures
            .filter(message => message.warningLevel === WarningLevel.ERROR)
            .map(failure => this.errorMessage(failure.message));
    }

    private renderWarnings(): TemplateResult[] {
        return this.validationFailures
            .filter(message => message.warningLevel === WarningLevel.WARNING)
            .map(failure => this.warningMessage(failure.message));
    }

    protected renderValidationIcon(): Nullable<TemplateResult> {
        const icon = this.renderIcon();
        if (!icon) {
            return null;
        }

        return html`
            <div class="validation-icon">${icon}</div>`;
    }

    protected basicClassMap(): DirectiveResult {
        return classMap({
            "valid": this.isInValidState(),
            "warning": this.isInWarningState(),
            "invalid": this.isInErrorState(),
            "required": this.required
        });
    }

    private renderIcon(): Nullable<TemplateResult> {
        if (this.isInErrorState()) {
            return html`<eop-svg-sprite-icon name="error"></eop-svg-sprite-icon>`;
        }
        if (this.isInWarningState()) {
            return html`<eop-svg-sprite-icon name="warn"></eop-svg-sprite-icon>`;
        }
        return null;
    }

    private validate = (silent: boolean = false): void => {
        if (this.hidden) {
            return;
        }
        if (!silent) {
            this.isValidatable = true;
            this.updateValidState(this.value);
        }
        if (this.hasErrors()) {
            throw new ValidationError(this);
        }
    };

    private sendInputEvent(inputValue: T): void {
        let customValues = new Map();
        if (this.enhanceValues === "true") {
            customValues = this.valueEnhancements(inputValue);
        }
        this.dispatchEvent(formElementEvent(this.path, inputValue, customValues));
    }

    protected valueEnhancements(inputValue: T): Map<string, string> {
        return new Map();
    }

    protected changeCondition(result: boolean): void {
        if (result) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    private expand(): void {
        if (this.hidden) {
            this.hidden = false;
            this.publishValue(this.value);
        }
    }

    private collapse(): void {
        if (!this.hidden) {
            this.hidden = true;
            this.publishValue(this.emptyValue);
        }
    }

    protected hasErrors(): boolean {
        return this.validationFailures.filter(message => message.warningLevel === WarningLevel.ERROR).isNotEmpty();
    }

    protected hasWarning(): boolean {
        return this.validationFailures.filter(value => value.warningLevel === WarningLevel.WARNING).isNotEmpty();
    }

    protected updateValidState(value: T): void {
        this.validationFailures = [];
        this.validations.forEach(config => ValidationFactory.from(config).accept(this, value));
    }

    protected labelSuffix(): TemplateResult | null {
        if (!this.labelSuffixPopoverId) {
            return null;
        }

        return html`
            <span class="label-suffix">
                <eop-i-button i-button-style="wrapped" popover-id=${this.labelSuffixPopoverId}></eop-i-button>
            </span>`;
    }

    protected errorMessage(message: string): TemplateResult {
        return html`
            <div class="error-message">${message}</div>`;
    }

    private warningMessage(message: string): TemplateResult {
        return html`
            <div class="warning-message">${message}</div>`;
    }

    protected isInValidState(): boolean {
        return this.isValidatable && !this.hasErrors();
    }

    protected isInErrorState(): boolean {
        return this.isValidatable && this.hasErrors();
    }

    protected isInWarningState(): boolean {
        return this.isValidatable && this.hasWarning() && !this.isInErrorState();
    }

    public setRequired(required: boolean): void {
        this.required = required;
    }

    public setType(type: InputElementType): void {
        this.type = type;
    }

    public addMessage(msg: ValidationFailure): void {
        this.validationFailures.push(msg);
    }

    public addCustomAttribute(attr: string, value: string): void {
        this.customAttributes[attr] = value;
    }

    protected preventLabelDefaultAction(event: Event): void {
        return event.preventDefault();
    }
}

export type ValidationFailure = {
    message: string;
    warningLevel: WarningLevel;
};